import React, { FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DRAFT_SYNC_LABELS, draftSyncState } from '../domain/drafts';
import type { DraftFields, UnsyncedDraft } from '../domain/drafts';
import { ADD_A_TITLE, KEEP_A_TITLE } from '../domain/listings';
import type { Listing, ListingType, PublishChoice } from '../domain/listings';
import { useOwnedCollections } from '../hooks/useCollections';
import { useDraftOutbox, useUnsyncedDraft } from '../hooks/useDraftOutbox';
import { useListing } from '../hooks/useListings';
import { useOnline } from '../hooks/useOnline';
import { useRepositories } from '../hooks/useRepositories';
import { useMyId } from '../hooks/useSession';
import ConfirmDialog from '../components/ConfirmDialog';
import { ActionError, LoadingState, NotFoundState, StateForError } from '../components/PageStates';
import PhotoEditor from '../components/PhotoEditor';
import PublishChoiceField from '../components/PublishChoiceField';
import UnsyncedPhotoEditor from '../components/UnsyncedPhotoEditor';
import { LISTING_TYPES, originOf, PUBLISH_SUBMIT_LABEL } from '../utils/listings';
import { isMe } from '../utils/people';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ErrorText } from '../components/ErrorText';
import { Trash2 } from 'lucide-react';

const DRAFTS_PATH = '/my-listings/drafts';

/**
 * `/listings/:id/edit` — the author changes a listing's content (016 FR-013,
 * FR-014): never its collection, and nobody is told. Emptying the price or
 * the expiry clears it. Photos are saved as they change. A draft may stay
 * untitled, and can be published or scheduled from here once it has a title
 * (021 FR-010, FR-012). Saving returns to the list the editor was opened
 * from (router state `from`), else to the listing (021 FR-011c).
 *
 * A draft this page made that hasn't synced yet opens from the page instead
 * (021 FR-009b): edits are saved here and go out with its sync, it can't be
 * published or scheduled until then, and deleting it wins over its sync.
 */
export default function EditListing() {
  const { id = '' } = useParams<{ id: string }>();
  return <EditorFor key={id} id={id} />;
}

/** Field edits not yet saved, kept here so they survive the editor changing mode. */
type Edits = { content: Content | null; onEdit: (content: Content) => void };

function EditorFor({ id }: { id: string }) {
  const sync = useDraftOutbox();
  const draft = useUnsyncedDraft(id);
  const [openedUnsynced] = useState(() => sync.outbox.get(id) !== undefined);
  const [synced, setSynced] = useState<Listing | null>(null);
  const [content, setContent] = useState<Content | null>(null);
  const edits: Edits = { content, onEdit: setContent };
  // A draft that syncs while open becomes an ordinary draft here, unsaved edits and all.
  useEffect(() => sync.onSynced((listing) => listing.id === id && setSynced(listing)), [sync, id]);

  if (synced) return <EditForm listing={synced} edits={edits} />;
  // Deleted here (or discarded on sign-out): the page is already on its way out.
  if (openedUnsynced) return draft ? <UnsyncedEditForm draft={draft} edits={edits} /> : null;
  return <ServerEditor id={id} edits={edits} />;
}

function ServerEditor({ id, edits }: { id: string; edits: Edits }) {
  const listing = useListing(id);
  const myId = useMyId();

  if (listing.status === 'loading') return <LoadingState label="Loading listing" />;
  if (listing.status === 'error') {
    return <StateForError error={listing.error} onRetry={listing.reload} variant="listing" />;
  }
  if (!isMe(listing.data.author, myId)) return <NotFoundState variant="listing" />;
  return <EditForm listing={listing.data} edits={edits} />;
}

/** `YYYY-MM-DDTHH:mm` in local time, as `datetime-local` takes it. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

type Content = {
  title: string;
  description: string;
  listingType: ListingType;
  price: string;
  location: string;
  expiresAt: string;
};

function contentOf(
  listing: Pick<
    DraftFields,
    'title' | 'description' | 'listingType' | 'price' | 'location' | 'expiresAt'
  >,
): Content {
  return {
    title: listing.title,
    description: listing.description,
    listingType: listing.listingType,
    price: listing.price ?? '',
    location: listing.location,
    expiresAt: toLocalInput(listing.expiresAt),
  };
}

/** The content as the service and the outbox take it: trimmed, empty price and expiry cleared. */
function changesOf(content: Content) {
  return {
    title: content.title.trim(),
    description: content.description.trim(),
    listingType: content.listingType,
    price: content.price === '' ? null : content.price,
    location: content.location.trim(),
    expiresAt: content.expiresAt ? new Date(content.expiresAt).toISOString() : null,
  };
}

function EditForm({ listing, edits }: { listing: Listing; edits: Edits }) {
  const navigate = useNavigate();
  const origin = originOf(useLocation().state);
  const { listings } = useRepositories();
  const isDraft = listing.publishState === 'draft';
  const mayClearTitle = isDraft && listing.announcedAt === null;
  const content = edits.content ?? contentOf(listing);
  const [publish, setPublish] = useState<PublishChoice>('draft');
  const [publishAt, setPublishAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const publishing = isDraft && publish !== 'draft';
    if (!content.title.trim() && (publishing || !mayClearTitle)) {
      setError(new Error(publishing ? ADD_A_TITLE : KEEP_A_TITLE));
      return;
    }
    if (publishing && publish === 'scheduled' && !publishAt) {
      setError(new Error('Choose when to publish it.'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await listings.update(listing.id, changesOf(content));
      if (publishing && publish === 'now') await listings.changeStatus(listing.id, 'publishNow');
      if (publishing && publish === 'scheduled') {
        await listings.changeStatus(listing.id, 'schedule', publishAt);
      }
      navigate(origin ?? `/listings/${listing.id}`);
    } catch (err) {
      setError(err);
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="text-4xl font-bold tracking-tight text-foreground">Edit listing</h1>

      <PhotoEditor listingId={listing.id} initialPhotos={listing.photos} />

      <form onSubmit={save} className="space-y-6 rounded-3xl bg-card p-6">
        {error !== null && <ActionError error={error} />}

        <ContentFields
          content={content}
          onChange={(changes) => edits.onEdit({ ...content, ...changes })}
        />

        {isDraft && (
          <PublishChoiceField
            publish={publish}
            onPublishChange={setPublish}
            publishAt={publishAt}
            onPublishAtChange={setPublishAt}
          />
        )}

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            className="rounded-full text-primary"
            onClick={() => navigate(origin ?? `/listings/${listing.id}`)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="submit" className="rounded-full" disabled={saving}>
            {saving ? 'Saving…' : isDraft ? PUBLISH_SUBMIT_LABEL[publish] : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * An unsynced draft, edited on the page (021 FR-009b). Saving bumps its
 * revision so sync carries the edit. Its collection opens only to answer
 * "Choose another collection" (FR-009). Publish waits for sync.
 */
function UnsyncedEditForm({ draft, edits }: { draft: UnsyncedDraft; edits: Edits }) {
  const navigate = useNavigate();
  const origin = originOf(useLocation().state) ?? DRAFTS_PATH;
  const { outbox } = useDraftOutbox();
  const online = useOnline();
  const content = edits.content ?? contentOf(draft.fields);
  const [collectionId, setCollectionId] = useState(draft.fields.collectionId);
  const [error, setError] = useState<unknown>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Only "Choose another collection" opens the collection; otherwise, as for any listing, it stays.
  const mayChooseCollection = !draft.serverConfirmed && draft.blocker === 'chooseCollection';

  const save = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const changes = changesOf(content);
    try {
      outbox.save(draft.id, {
        fields: mayChooseCollection ? { ...changes, collectionId } : changes,
      });
      navigate(origin);
    } catch (err) {
      setError(err);
    }
  };

  const state = draftSyncState(draft, online);

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="space-y-1">
        <h1 className="text-4xl font-bold tracking-tight text-foreground">Edit listing</h1>
        <p className="text-sm text-muted-foreground">{DRAFT_SYNC_LABELS[state]}</p>
      </div>

      <UnsyncedPhotoEditor draft={draft} />

      <form onSubmit={save} className="space-y-6 rounded-3xl bg-card p-6">
        {error !== null && <ActionError error={error} />}

        {mayChooseCollection && <CollectionField value={collectionId} onChange={setCollectionId} />}

        <ContentFields
          content={content}
          onChange={(changes) => edits.onEdit({ ...content, ...changes })}
        />

        <div className="flex flex-wrap items-start justify-between gap-3">
          <Button
            type="button"
            variant="ghost"
            className="rounded-full text-destructive"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 />
            Delete draft
          </Button>
          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-3">
              <Button
                type="button"
                variant="ghost"
                className="rounded-full text-primary"
                onClick={() => navigate(origin)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="secondary" className="rounded-full">
                Save draft
              </Button>
              <Button
                type="button"
                className="rounded-full"
                disabled
                aria-describedby="publishes-once-synced"
              >
                Publish
              </Button>
            </div>
            <p id="publishes-once-synced" className="text-sm text-muted-foreground">
              Publishes once synced
            </p>
          </div>
        </div>
      </form>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete draft?"
        description={<p>It will be gone from this page and won't be synced.</p>}
        confirmLabel="Delete"
        onConfirm={() => {
          outbox.remove(draft.id);
          navigate(origin);
        }}
      />
    </div>
  );
}

/** Where an unsynced draft goes; only my collections (FR-003). */
function CollectionField({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const owned = useOwnedCollections();
  const mine = owned.status === 'ready' ? owned.data.collections : [];
  const chosen = mine.some((c) => c.id === value) ? value : '';
  return (
    <div>
      <Label id="unsynced-collection-label">Collection</Label>
      <ErrorText className="mt-1 text-sm font-medium">
        {DRAFT_SYNC_LABELS.chooseCollection}
      </ErrorText>
      {owned.status === 'error' ? (
        <p className="mt-1 text-sm text-muted-foreground">Connect to choose a collection.</p>
      ) : (
        <Select value={chosen} onValueChange={onChange} disabled={owned.status === 'loading'}>
          <SelectTrigger aria-labelledby="unsynced-collection-label" className="mt-1">
            <SelectValue placeholder="Choose a collection" />
          </SelectTrigger>
          <SelectContent>
            {mine.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

function ContentFields({
  content,
  onChange,
}: {
  content: Content;
  onChange: (changes: Partial<Content>) => void;
}) {
  return (
    <>
      <div>
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={content.title}
          onChange={(e) => onChange({ title: e.target.value })}
          maxLength={200}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea
          id="description"
          value={content.description}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={4}
          className="mt-1"
        />
      </div>

      <div>
        <Label id="listing-type-label">Type</Label>
        <Select
          value={content.listingType}
          onValueChange={(v) => onChange({ listingType: v as ListingType })}
        >
          <SelectTrigger aria-labelledby="listing-type-label" className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LISTING_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <Label htmlFor="price">Price (optional)</Label>
          <InputGroup className="mt-1">
            <InputGroupAddon aria-hidden="true">$</InputGroupAddon>
            <InputGroupInput
              type="number"
              id="price"
              value={content.price}
              onChange={(e) => onChange({ price: e.target.value })}
              step="0.01"
              min="0"
              placeholder="No price"
              className="[-moz-appearance:textfield] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </InputGroup>
        </div>
        <div>
          <Label htmlFor="location">Location (optional)</Label>
          <Input
            id="location"
            value={content.location}
            onChange={(e) => onChange({ location: e.target.value })}
            maxLength={100}
            className="mt-1"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="expires_at">Expires (optional)</Label>
        <Input
          type="datetime-local"
          id="expires_at"
          value={content.expiresAt}
          onChange={(e) => onChange({ expiresAt: e.target.value })}
          className="mt-1"
        />
      </div>
    </>
  );
}
